import { useIsMutating, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/core/auth";
import type { IUser } from "@/core/auth/auth";
import ImpersonationContext from "./impersonation-context";
import { impersonationOptionsKey } from "./impersonation-options";
import {
  impersonationSessionMutationKey,
  impersonationStatusKey,
  useEndImpersonation,
  useImpersonationStatus,
  useStartImpersonation,
} from "./impersonation-query";
import type { ImpersonationStatusResponse } from "./impersonation-query";

interface ImpersonationProviderProps {
  children: React.ReactNode;
}

const pendingSaveFilter = {
  predicate: (mutation: { options: { mutationKey?: readonly unknown[] } }) =>
    mutation.options.mutationKey !== impersonationSessionMutationKey,
};

const ImpersonationProvider = ({ children }: ImpersonationProviderProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    handleImpersonationChange,
    isAuthenticated,
    impersonatedUser: authImpersonatedUser,
  } = useAuth();

  const [isChangingSession, setIsChangingSession] = useState(false);
  const sessionRequestPending = useRef(false);
  const startMutation = useStartImpersonation();
  const endMutation = useEndImpersonation();
  const { data: impersonationData } = useImpersonationStatus(
    !startMutation.isPending && !endMutation.isPending && !isChangingSession,
  );

  const [impersonatedUser, setImpersonatedUser] = useState<IUser | null>(
    authImpersonatedUser,
  );
  const isImpersonating = !!impersonatedUser;

  const isApplyingSession = useRef(false);
  const authenticated = useRef(isAuthenticated);
  authenticated.current = isAuthenticated;
  const appliedUserId = useRef<string | null>(authImpersonatedUser?.id ?? null);
  const isSavePending = useIsMutating(pendingSaveFilter) > 0;

  const applySession = useCallback(
    async (response: ImpersonationStatusResponse, openProduct = false) => {
      isApplyingSession.current = true;
      // Leave the effect lifecycle before synchronously unmounting page observers.
      await Promise.resolve();
      flushSync(() => setIsChangingSession(true));
      await queryClient.cancelQueries();

      // Another tab can replace the server session while a local save is running.
      // Let its callbacks finish, then remove anything they wrote to the old cache.
      if (queryClient.isMutating(pendingSaveFilter)) {
        await new Promise<void>((resolve) => {
          const unsubscribe = queryClient.getMutationCache().subscribe(() => {
            if (!queryClient.isMutating(pendingSaveFilter)) {
              unsubscribe();
              resolve();
            }
          });
        });
      }
      if (!authenticated.current) {
        isApplyingSession.current = false;
        setIsChangingSession(false);
        return;
      }
      queryClient.removeQueries({
        predicate: ({ queryKey }) =>
          !(
            queryKey[0] === impersonationOptionsKey[0] &&
            (queryKey[1] === impersonationOptionsKey[1] ||
              queryKey[1] === "status")
          ),
      });
      queryClient.getMutationCache().clear();
      queryClient.setQueryData(impersonationStatusKey(), response);
      appliedUserId.current = response.impersonated_user?.id ?? null;
      flushSync(() => {
        setImpersonatedUser(response.impersonated_user ?? null);
        handleImpersonationChange({
          isImpersonating: !!response.impersonated_user,
          impersonatedUser: response.impersonated_user ?? null,
        });
        if (openProduct && response.impersonated_user) {
          navigate("/", { replace: true, state: { fromImpersonation: true } });
        }
        setIsChangingSession(false);
      });
      isApplyingSession.current = false;
    },
    [queryClient, handleImpersonationChange, navigate],
  );

  useEffect(() => {
    if (!isAuthenticated) {
      appliedUserId.current = null;
      setImpersonatedUser(null);
      return;
    }
    if (
      !impersonationData ||
      sessionRequestPending.current ||
      isApplyingSession.current
    )
      return;
    const user = impersonationData.impersonated_user ?? null;
    if (appliedUserId.current !== (user?.id ?? null)) {
      void applySession(impersonationData);
      return;
    }
    appliedUserId.current = user?.id ?? null;
    setImpersonatedUser(user);
    handleImpersonationChange({
      isImpersonating: !!user,
      impersonatedUser: user,
    });
  }, [
    impersonationData,
    handleImpersonationChange,
    isAuthenticated,
    applySession,
  ]);

  const changeSession = async (
    request: () => Promise<ImpersonationStatusResponse>,
    openProduct = false,
  ): Promise<void> => {
    if (sessionRequestPending.current) {
      throw new Error("A session change is already in progress. Please wait.");
    }
    if (queryClient.isMutating(pendingSaveFilter)) {
      throw new Error(
        "A save is in progress. Wait for it to finish before switching users.",
      );
    }
    sessionRequestPending.current = true;
    try {
      // A status response from before replacement must not overwrite the new session.
      await queryClient.cancelQueries({ queryKey: impersonationStatusKey() });
      const response = await request();

      await applySession(response, openProduct);
    } finally {
      sessionRequestPending.current = false;
    }
  };

  const startImpersonation = (userId: string): Promise<void> =>
    changeSession(() => startMutation.mutateAsync(userId), true);

  const endImpersonation = (): Promise<void> =>
    changeSession(() => endMutation.mutateAsync());

  const contextValue = {
    isImpersonating,
    impersonatedUser,
    isSavePending,
    startImpersonation,
    endImpersonation,
  };

  return (
    <ImpersonationContext.Provider value={contextValue}>
      {isChangingSession ? (
        <div role="status" className="p-6 text-xs">
          Loading user data...
        </div>
      ) : (
        children
      )}
    </ImpersonationContext.Provider>
  );
};

export default ImpersonationProvider;
